import type { ActivityKind } from "./thera-data";
import { openDeviceDatabase, ROUTE_DRAFT_STORE } from "./device-storage";
import type { RouteGeoJson } from "./route-workflow";

export type LocalRoutePhoto = {
  id: string;
  name: string;
  blob: Blob;
  before: string;
  after: string;
  width: number;
  height: number;
  altText: string;
};

export type LocalRoutePanorama = {
  id: string;
  name: string;
  blob: Blob;
  width: number;
  height: number;
  stopName: string;
  distanceKm: number;
  coverage: "spherical" | "cylindrical";
};

export type LocalRouteFile = {
  name: string;
  blob: Blob;
  mimeType: string;
};

export type LocalRouteDraft = {
  storageKey: string;
  id: string;
  ownerId: string;
  name: string;
  area: string;
  kind: ActivityKind;
  description: string;
  recommendations: string;
  distanceM: number;
  ascentM: number;
  estimatedSeconds: number;
  pathGeojson: RouteGeoJson;
  gpxFile: LocalRouteFile | null;
  photos: LocalRoutePhoto[];
  /** Beta privada: todavía no se sincroniza ni se publica. */
  panoramas?: LocalRoutePanorama[];
  sourcePlatform: "web" | "android" | "import";
  updatedAt: number;
};

function key(ownerId: string, id: string) {
  return `${ownerId}:${id}`;
}

export async function saveLocalRouteDraft(
  input: Omit<LocalRouteDraft, "storageKey" | "updatedAt">,
) {
  if (!("indexedDB" in window)) return;
  const database = await openDeviceDatabase();
  const value: LocalRouteDraft = {
    ...input,
    storageKey: key(input.ownerId, input.id),
    updatedAt: Date.now(),
  };
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(ROUTE_DRAFT_STORE, "readwrite");
    transaction.objectStore(ROUTE_DRAFT_STORE).put(value);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function listLocalRouteDrafts(ownerId: string): Promise<LocalRouteDraft[]> {
  if (!("indexedDB" in window)) return [];
  const database = await openDeviceDatabase();
  const values = await new Promise<LocalRouteDraft[]>((resolve, reject) => {
    const request = database
      .transaction(ROUTE_DRAFT_STORE, "readonly")
      .objectStore(ROUTE_DRAFT_STORE)
      .getAll();
    request.onsuccess = () => resolve(request.result as LocalRouteDraft[]);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return values
    .filter((draft) => draft.ownerId === ownerId)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteLocalRouteDraft(ownerId: string, id: string) {
  if (!("indexedDB" in window)) return;
  const database = await openDeviceDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(ROUTE_DRAFT_STORE, "readwrite");
    transaction.objectStore(ROUTE_DRAFT_STORE).delete(key(ownerId, id));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}
